import type { ReasoningApi } from '../api/reasoning-api.interface';
import type { FetchReasoningParams, ReasoningService, SaveReasoningAsCommentParams } from './reasoning-service.interface';

export class ReasoningServiceImpl implements ReasoningService {
    private inflight = new Map<string, AbortController>();

    constructor(private readonly reasoningApi: ReasoningApi) {}

    async fetchReasoning(params: FetchReasoningParams): Promise<string> {
        const key = this.toKey(params);
        this.cancel(key);
        const ctrl = new AbortController();
        this.inflight.set(key, ctrl);
        try {
            const resp = await this.reasoningApi.getReasoning({
                folder_id: params.folderId,
                field_id: params.fieldId,
                patent_id: params.patentId,
                signal: ctrl.signal,
            });
            return (resp?.reasoning ?? '').trim();
        } finally {
            if (this.inflight.get(key) === ctrl) {
                this.inflight.delete(key);
            }
        }
    }

    cancel(key?: string): void {
        if (key) {
            const ctrl = this.inflight.get(key);
            if (ctrl) {
                ctrl.abort();
                this.inflight.delete(key);
            }
            return;
        }
        this.inflight.forEach(ctrl => ctrl.abort());
        this.inflight.clear();
    }

    saveAsComment(params: SaveReasoningAsCommentParams): Promise<boolean> {
        return this.reasoningApi.checkPatentWithReasoning({
            folder_id: params.folderId,
            field_id: params.fieldId,
            patent_id: params.patentId,
            save_reasoning_as_comment: params.saveReasoningAsComment,
            comment_visible_scope: params.commentVisibleScope,
        });
    }

    private toKey(params: FetchReasoningParams): string {
        return `${params.folderId}|${params.fieldId}|${params.patentId}`;
    }
}
